import { z } from 'zod'

import {
	ApplicationError,
	type DApp,
	type Template,
	type UseCase
} from '../domain'

const restrictedStrings = ['codigo', 'null', 'undefined', 'SSL']

export abstract class ForManagingDApps {
	static CreateDAppBasePayload = z
		.object({
			// TODO: publicProjectId should be a separate type
			// it's used in multiple places
			publicProjectId: z
				.string()
				.min(6, {
					message: 'The project id must be at least 6 characters long'
				})
				.max(30, {
					message: 'The project id must be at most 30 characters long'
				})
				.regex(/^[a-z]/, {
					message: 'The project id must start with a lowercase letter'
				})
				.regex(/[a-z0-9]$/, {
					message: 'The project id cannot end with a hyphen'
				})
				.regex(/^[a-z][a-z0-9-]*[a-z0-9]$/, {
					message:
						'The project id can only contain lowercase letters, numbers, and hyphens'
				})
				.refine(val => !restrictedStrings.includes(val), {
					message:
						"The project id cannot contain restricted strings such as 'codigo', 'null', 'undefined', and 'SSL'"
				}),
			name: z
				.string()
				.min(3, { message: 'The name must be at least 3 characters long' })
				.max(32, { message: 'The name must be at most 32 characters long' })
				.regex(/^[a-zA-Z]/, { message: 'The name must start with a letter' })
				.regex(/^(?!.*[- ]$)(?!^[- ])/, {
					message: 'The name cannot start or end with a space or hyphen'
				})
				.regex(/^[a-zA-Z0-9-]+$/, {
					message: 'The name can only contain letters, numbers, and hyphens'
				}),
			description: z
				.string()
				.max(160, {
					message: 'The description must be at most 160 characters long'
				})
				.optional()
		})
		.required()

	static CreateDAppFromTemplatePayload = this.CreateDAppBasePayload.extend({
		templateId: z.string().min(1, {
			message: 'Please select a template'
		})
	})

	static CreateDAppFromPromptPayload = this.CreateDAppBasePayload.extend({
		userPrompt: z.string({
			message: 'Please enter a prompt'
		}),
		useCaseIds: z.array(z.string()).min(1, {
			message: 'Please select at least one use case'
		})
	})

	static CreateDAppFromGithubPayload = this.CreateDAppBasePayload.extend({
		githubUrl: z.string().url({
			message: 'The URL must be a valid URL'
		}),
		// Note: `githubRepoLatestCommit` is optional in the frontend validation schema
		// because the frontend only sends `githubUrl`. The DApp service is responsible
		// for fetching the latest commit SHA and passing it to the adapter.
		// However, this field is required in the adapter for making the API request.
		// Consider whether it's appropriate to separate frontend and backend validation schemas
		// to clearly distinguish between what the frontend sends and what the backend needs.
		githubRepoLatestCommit: z.string().optional()
	})

	abstract createDAppFromTemplate(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromTemplatePayload>
	): Promise<{
		data?: DApp
		error?: ApplicationError
	}>

	abstract createDAppFromPrompt(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromPromptPayload>
	): Promise<{
		data?: DApp
		error?: ApplicationError
	}>

	abstract createDAppFromGithub(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromGithubPayload>
	): Promise<{
		data?: DApp
		error?: ApplicationError
	}>

	abstract openWorkspace(dappId: string): Promise<{
		error?: ApplicationError
	}>

	abstract isWorkspaceHealthy(workspaceUrl: string): Promise<{
		data?: boolean
		error?: ApplicationError
	}>

	abstract getTemplates(): Promise<{
		data?: Template[]
		error?: ApplicationError
	}>

	abstract getUseCases(): Promise<{
		data?: UseCase[]
		error?: ApplicationError
	}>
}
